import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { 
  Loader2, Play, RefreshCw, Cpu, CheckCircle2, XCircle, 
  Clock, Power, AlertCircle, ShieldAlert 
} from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";
import { supabase } from "../../../lib/supabase";

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
const SCHEDULER_DIRECT_URL = (import.meta as any).env?.VITE_RUST_SCHEDULER_URL || 'http://localhost:8085';

const SCRAPERS = [
  { 
    id: '5pct', 
    label: 'Kepemilikan 5%', 
    description: 'Laporan kepemilikan saham ≥5%', 
    timeKey: 'scraper_5pct_time',
    defaultTime: '20:57 WIB',
    condition: 'Setiap Hari Bursa',
    runTodayKey: 'scraper_5pct_run_today',
    lastRunKey: 'last_5pct_run',
    color: 'bg-blue-600 hover:bg-blue-700' 
  },
  { 
    id: 'hcl', 
    label: 'HCL (Konsentrasi)', 
    description: 'Kepemilikan saham terkonsentrasi tinggi', 
    timeKey: 'scraper_hcl_time',
    defaultTime: '21:02 WIB',
    condition: 'Setiap Hari Bursa',
    runTodayKey: 'scraper_hcl_run_today',
    lastRunKey: 'last_hcl_run',
    color: 'bg-amber-600 hover:bg-amber-700' 
  },
  { 
    id: '1pct', 
    label: 'Kepemilikan 1%', 
    description: 'Laporan kepemilikan saham ≥1%', 
    timeKey: 'scraper_1pct_time',
    defaultTime: '21:03 WIB',
    condition: 'H-1 Akhir Bulan Saja',
    runTodayKey: 'scraper_1pct_run_today',
    lastRunKey: 'last_1pct_run',
    color: 'bg-violet-600 hover:bg-violet-700' 
  },
  { 
    id: 'ff', 
    label: 'Free Float (FF)', 
    description: 'Laporan free float (bulanan)',
    timeKey: 'scraper_ff_time',
    defaultTime: '21:07 WIB',
    condition: 'Setiap Hari Bursa',
    runTodayKey: 'scraper_ff_run_today',
    lastRunKey: 'last_ff_run',
    color: 'bg-emerald-600 hover:bg-emerald-700' 
  },
  { 
    id: 'perubahan', 
    label: 'Perubahan Kepemilikan', 
    description: 'Laporan perubahan kepemilikan KSEI', 
    timeKey: 'scraper_perubahan_time',
    defaultTime: '21:12 WIB',
    condition: 'Memicu VM1 Auto-Deallocate',
    runTodayKey: 'scraper_perubahan_run_today',
    lastRunKey: 'last_perubahan_run',
    color: 'bg-rose-600 hover:bg-rose-700' 
  },
] as const;

type ScraperId = typeof SCRAPERS[number]['id'];

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

export function PythonScraperControl() {
  const { showToast } = useToast();
  
  // State
  const [schedulerStatus, setSchedulerStatus] = useState<any>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState<string | null>(null);
  const [deallocatingVm1, setDeallocatingVm1] = useState(false);
  const [autoDeallocAfterManual, setAutoDeallocAfterManual] = useState(false);
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const isPollingRef = useRef(false);

  // Fetch Scheduler Status (Direct with fallback via backend proxy)
  const fetchStatus = useCallback(async () => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    setLoadingStatus(true);

    try {
      let data: any = null;

      // 1. Try Direct Scheduler endpoint
      try {
        const directRes = await fetch(`${SCHEDULER_DIRECT_URL}/api/status`, {
          signal: AbortSignal.timeout(2500)
        });
        if (directRes.ok) {
          const directJson = await directRes.json();
          data = directJson.data || directJson;
        }
      } catch {
        // Direct failed, will fallback to backend proxy
      }

      // 2. Fallback to Backend Proxy
      if (!data) {
        const headers = await getAuthHeaders();
        const proxyRes = await fetch(`${API_URL}/api/developer/rust/scheduler-status`, { 
          headers,
          signal: AbortSignal.timeout(4000)
        });
        if (proxyRes.ok) {
          const proxyJson = await proxyRes.json();
          data = proxyJson.data || proxyJson;
        }
      }

      if (data) {
        setSchedulerStatus(data);
        const state = data.state || data;
        if (state.recent_python_runs && Array.isArray(state.recent_python_runs)) {
          setRecentRuns(state.recent_python_runs);
        } else {
          // Fallback to fetch /python-scrapers/runs
          fetchRunsFallback();
        }
      }
    } catch (e) {
      console.error('Error fetching scheduler status:', e);
    } finally {
      setLoadingStatus(false);
      isPollingRef.current = false;
    }
  }, []);

  const fetchRunsFallback = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/developer/python-scrapers/runs?per_page=10`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.ok && Array.isArray(data.data)) {
          setRecentRuns(data.data);
        }
      }
    } catch (e) {
      console.error('Fallback fetch runs error:', e);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Periodic polling
  useEffect(() => {
    const isBusy = schedulerStatus?.state?.is_busy || schedulerStatus?.is_busy || triggeringJob !== null;
    const intervalMs = isBusy ? 4000 : 10000;

    const interval = setInterval(() => {
      fetchStatus();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [fetchStatus, schedulerStatus, triggeringJob]);

  // Trigger Scraper
  const handleTrigger = async (job: string, deallocateAfter?: boolean) => {
    setTriggeringJob(job);
    const dealloc = deallocateAfter ?? autoDeallocAfterManual;

    try {
      let success = false;
      let resultMsg = '';

      // 1. Try direct call to scheduler
      try {
        const directRes = await fetch(`${SCHEDULER_DIRECT_URL}/api/trigger/python-scrapers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ job, deallocate_after: dealloc }),
          signal: AbortSignal.timeout(300000), // Scraper runs can take 1-3 minutes
        });

        if (directRes.ok) {
          const resJson = await directRes.json();
          success = resJson.ok ?? true;
          resultMsg = resJson.message || resJson.data || `Scraper ${job} executed`;
        }
      } catch {
        // Fallback to backend proxy
      }

      // 2. Fallback via backend proxy
      if (!success) {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_URL}/api/developer/python-scrapers/trigger`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ job, deallocate_after: dealloc }),
        });

        const resJson = await res.json().catch(() => ({}));
        if (!res.ok || resJson.ok === false) {
          throw new Error(resJson.message || `HTTP ${res.status}`);
        }
        resultMsg = resJson.message || `Scraper ${job} triggered successfully`;
      }

      showToast({
        type: 'success',
        title: 'Scraper Selesai',
        message: resultMsg,
      });

      // Refresh status immediately
      fetchStatus();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Trigger Gagal',
        message: err.message || 'Gagal menjalankan scraper',
      });
    } finally {
      setTriggeringJob(null);
    }
  };

  // Manual VM1 Deallocate
  const handleDeallocateVm1 = async () => {
    const confirmed = window.confirm("Konfirmasi untuk deallocate VM1 sekarang via Azure API?");
    if (!confirmed) return;

    setDeallocatingVm1(true);
    try {
      let success = false;
      let resultMsg = '';

      // Try direct scheduler endpoint
      try {
        const directRes = await fetch(`${SCHEDULER_DIRECT_URL}/api/vms/deallocate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vm_index: 1 }),
          signal: AbortSignal.timeout(30000),
        });
        if (directRes.ok) {
          const directJson = await directRes.json();
          success = directJson.ok ?? true;
          resultMsg = directJson.message || 'VM1 deallocated successfully';
        }
      } catch {
        // Fallback
      }

      // Fallback via backend
      if (!success) {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_URL}/api/developer/rust/direct-stop-vms`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ vm_index: 1 }),
        });
        const resJson = await res.json().catch(() => ({}));
        if (!res.ok || resJson.ok === false) {
          throw new Error(resJson.message || `HTTP ${res.status}`);
        }
        resultMsg = resJson.message || 'VM1 deallocated successfully';
      }

      showToast({
        type: 'success',
        title: 'VM1 Deallocated',
        message: resultMsg,
      });
      fetchStatus();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Deallocate Gagal',
        message: err.message || 'Gagal mematikan VM1',
      });
    } finally {
      setDeallocatingVm1(false);
    }
  };

  const state = schedulerStatus?.state || schedulerStatus;
  const isOnline = schedulerStatus?.online !== false && state?.current_wib_time && !state?.current_wib_time.includes("Offline");
  const isBusy = state?.is_busy || triggeringJob !== null;

  return (
    <Card className="w-full border-zinc-200 dark:border-zinc-800 shadow-sm">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b dark:border-zinc-800">
        <div>
          <CardTitle className="flex items-center gap-2 text-xl font-bold">
            <Cpu className="h-5 w-5 text-orange-500" />
            Python Scrapers (Direct Scheduler Engine)
          </CardTitle>
          <CardDescription>
            Kontrol dan monitoring 5 scraper kepemilikan saham langsung via Rust Scheduler di VM1 (20:57 - 21:12 WIB)
          </CardDescription>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={isOnline ? "default" : "secondary"} className="text-xs">
            Scheduler: {isOnline ? "ONLINE (8085)" : "OFFLINE"}
          </Badge>
          <Badge variant={isBusy ? "destructive" : "outline"} className={`text-xs gap-1 ${!isBusy && isOnline ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 border-emerald-300" : ""}`}>
            {isBusy ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> VM1: BUSY / RUNNING</>
            ) : (
              <><CheckCircle2 className="h-3 w-3 text-emerald-500" /> VM1: IDLE</>
            )}
          </Badge>
          {state?.current_wib_time && (
            <Badge variant="outline" className="text-xs font-mono text-zinc-600 dark:text-zinc-400">
              {state.current_wib_time}
            </Badge>
          )}
          <Button onClick={fetchStatus} size="sm" variant="outline" disabled={loadingStatus} title="Refresh live status">
            <RefreshCw className={`h-4 w-4 ${loadingStatus ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-5">
        {/* Banner: VM1 Auto-Deallocation Status */}
        <div className="p-3.5 rounded-lg border border-orange-200/70 dark:border-orange-900/40 bg-gradient-to-r from-orange-50/50 via-amber-50/30 to-zinc-50 dark:from-orange-950/20 dark:via-amber-950/10 dark:to-zinc-900/40 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 font-semibold text-zinc-800 dark:text-zinc-200">
              <ShieldAlert className="h-4 w-4 text-orange-600 dark:text-orange-400 shrink-0" />
              <span>Aturan Auto-Deallocate VM1 Azure</span>
              <Badge variant="secondary" className="text-[10px] ml-1">Hemat Biaya Azure</Badge>
            </div>
            <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed text-[11px]">
              VM2 & VM3 mati jam <strong>20:00 WIB</strong>. VM1 tetap hidup untuk 5 scraper malam, lalu <strong>otomatis di-deallocate segera setelah scraper 21:12 (Perubahan) selesai</strong>.
            </p>
            {state?.last_vm1_deallocate_run && (
              <p className="text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                Terakhir: {state.last_vm1_deallocate_run}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={handleDeallocateVm1}
              disabled={deallocatingVm1 || isBusy}
              size="sm"
              variant="destructive"
              className="text-xs gap-1.5 h-8"
              title="Deallocate VM1 langsung di Azure sekarang"
            >
              {deallocatingVm1 ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Mematikan...</>
              ) : (
                <><Power className="h-3.5 w-3.5" /> Deallocate VM1 Now</>
              )}
            </Button>
            <Button
              onClick={() => handleTrigger('all', true)}
              disabled={isBusy}
              size="sm"
              className="text-xs gap-1.5 h-8 bg-zinc-800 hover:bg-zinc-900 text-white dark:bg-zinc-700 dark:hover:bg-zinc-600"
              title="Jalankan seluruh 5 scraper berurutan dan deallocate VM1"
            >
              {triggeringJob === 'all' ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running All...</>
              ) : (
                <><Play className="h-3.5 w-3.5" /> Run All (5 Scrapers)</>
              )}
            </Button>
          </div>
        </div>

        {/* Status Message from Scheduler Engine */}
        {state?.last_status_message && (
          <div className="flex items-center gap-2 px-3 py-2 text-xs bg-zinc-100 dark:bg-zinc-900/60 rounded-md border text-zinc-700 dark:text-zinc-300">
            <Clock className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <span className="font-semibold text-zinc-500 shrink-0">Status Engine:</span>
            <span className="font-mono truncate">{state.last_status_message}</span>
          </div>
        )}

        {/* 5 Scrapers Trigger Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {SCRAPERS.map(scraper => {
            const isThisJobRunning = triggeringJob === scraper.id;
            const scheduledTime = (state && state[scraper.timeKey]) || scraper.defaultTime;
            const ranToday = state && state[scraper.runTodayKey] === true;
            const lastRun = state && state[scraper.lastRunKey];

            return (
              <div 
                key={scraper.id} 
                className="flex flex-col justify-between p-3.5 border rounded-xl bg-zinc-50/60 dark:bg-zinc-900/50 hover:border-zinc-300 dark:hover:border-zinc-700 transition-all gap-3"
              >
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200 truncate">
                      {scraper.label}
                    </span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-mono font-normal">
                      {scheduledTime}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-zinc-400 line-clamp-2 leading-tight">
                    {scraper.description}
                  </p>
                  
                  <div className="pt-1 flex flex-wrap items-center gap-1">
                    <Badge 
                      variant={ranToday ? "default" : "secondary"} 
                      className={`text-[9px] px-1.5 py-0 h-4 ${ranToday ? "bg-emerald-500 text-white" : ""}`}
                    >
                      {ranToday ? "✓ Ran Today" : "Scheduled"}
                    </Badge>
                    <span className="text-[9px] text-zinc-400 truncate max-w-[120px]" title={scraper.condition}>
                      {scraper.condition}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 pt-1 border-t border-zinc-100 dark:border-zinc-800">
                  {lastRun && (
                    <div className="text-[9px] text-zinc-500 dark:text-zinc-400 truncate" title={lastRun}>
                      {lastRun.startsWith('SUCCESS') ? '✓ ' : lastRun.startsWith('FAILED') ? '✗ ' : ''}
                      {lastRun}
                    </div>
                  )}

                  <Button
                    onClick={() => handleTrigger(scraper.id, scraper.id === 'perubahan' ? true : autoDeallocAfterManual)}
                    disabled={isBusy}
                    size="sm"
                    className={`w-full text-white text-xs h-8 ${scraper.color}`}
                  >
                    {isThisJobRunning ? (
                      <><Loader2 className="h-3 w-3 animate-spin mr-1" />Executing...</>
                    ) : (
                      <><Play className="h-3 w-3 mr-1" />Run</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Options Bar */}
        <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400 px-1">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input 
              type="checkbox" 
              checked={autoDeallocAfterManual}
              onChange={(e) => setAutoDeallocAfterManual(e.target.checked)}
              className="rounded border-zinc-300 dark:border-zinc-700 text-orange-600 focus:ring-orange-500 h-3.5 w-3.5"
            />
            <span>Auto-deallocate VM1 setelah task manual selesai (kecuali Perubahan yang otomatis deallocate)</span>
          </label>
        </div>

        {/* Recent Scraper Runs Section */}
        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold flex items-center gap-1.5 text-zinc-800 dark:text-zinc-200">
              <RefreshCw className="h-3.5 w-3.5 text-orange-500" />
              Recent Scraper Runs (Live Scheduler History)
            </span>
            <span className="text-[11px] text-zinc-400">
              {recentRuns.length} riwayat tercatat
            </span>
          </div>

          <div className="border rounded-xl overflow-hidden divide-y divide-zinc-100 dark:divide-zinc-800 bg-zinc-50/40 dark:bg-zinc-950/20">
            {loadingStatus && recentRuns.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-xs text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Memuat riwayat scheduler...
              </div>
            ) : recentRuns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-xs text-zinc-400 gap-1">
                <span>Belum ada riwayat scraper hari ini</span>
                <span className="text-[10px] text-zinc-500">Scraper terjadwal otomatis mulai 20:57 WIB</span>
              </div>
            ) : (
              recentRuns.map((run: any) => {
                const status = (run.status || '').toLowerCase();
                const isSuccess = status === 'success' || status === 'completed';
                const isFailure = status === 'failure' || status === 'error';
                const isSkipped = status === 'skipped';
                const isRunning = status === 'running';

                return (
                  <div key={run.id || `${run.job}-${run.triggered_at || run.startedAt}`} className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-2.5 text-xs gap-2 hover:bg-zinc-50 dark:hover:bg-zinc-900/40 transition-colors">
                    <div className="flex items-start sm:items-center gap-2.5 min-w-0">
                      <span className="font-bold text-zinc-700 dark:text-zinc-200 tracking-wide uppercase shrink-0">
                        {run.job}
                      </span>
                      <span className="text-zinc-400 text-[11px] shrink-0 font-mono">
                        {run.triggered_at || (run.startedAt ? new Date(run.startedAt).toLocaleString('id-ID', { timeStyle: 'short', dateStyle: 'short' }) : '')}
                      </span>
                      {run.message && (
                        <span className="text-zinc-500 dark:text-zinc-400 text-[11px] truncate max-w-sm sm:max-w-md lg:max-w-lg hidden md:inline" title={run.message}>
                          — {run.message}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <Badge
                        variant={isSuccess ? "default" : isFailure ? "destructive" : "secondary"}
                        className={`text-[10px] px-2 py-0.5 font-medium ${
                          isSuccess ? "bg-emerald-500 hover:bg-emerald-600 text-white" : 
                          isSkipped ? "bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300" : ""
                        }`}
                      >
                        {isSuccess ? "✓ Success" : isFailure ? "✗ Error" : isSkipped ? "⏭ Skipped" : isRunning ? "⏳ Running" : run.status}
                      </Badge>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
